import { useMemo } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { auditThemeColors, formatRatio, type ThemeColors } from '@/theme/contrast';
import { cn } from '@/lib/utils';

interface ContrastAuditProps {
  colors: ThemeColors;
  className?: string;
}

/**
 * Live WCAG AA contrast feedback for the colours currently in the theme editor.
 *
 * The static suite in theme/__a11y__ proves the DEFAULT palette is accessible.
 * It says nothing about a tenant's custom theme, because those colours arrive
 * at runtime from the database — so a designer could publish a primary button
 * whose label is unreadable and nothing would object. This is that objection,
 * placed where the choice is made rather than in a report nobody opens.
 *
 * ## Why it warns rather than blocks
 *
 * Publishing stays possible. Contrast is a floor for body copy and controls,
 * but a designer working on a decorative section has legitimate reasons to sit
 * below it, and a gate they cannot pass is a gate they will route around —
 * here, by not using the customiser at all. Naming the specific pair and the
 * specific fix is what actually changes the outcome.
 */
export default function ContrastAudit({ colors, className }: ContrastAuditProps) {
  const checks = useMemo(() => auditThemeColors(colors), [colors]);
  const failures = checks.filter((c) => !c.passes);

  // Nothing to say yet: every field is blank, or a value is still half-typed.
  // A panel that shouts on first load gets collapsed and never reopened.
  if (checks.length === 0) return null;

  return (
    <section
      className={cn('rounded-lg border border-line bg-surface p-4', className)}
      aria-label="Colour contrast"
    >
      <header className="flex items-center gap-2">
        {failures.length === 0 ? (
          <>
            <Check className="h-4 w-4 text-success-600" aria-hidden="true" />
            <h3 className="text-sm font-medium text-ink">
              Contrast passes WCAG AA
            </h3>
          </>
        ) : (
          <>
            <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" />
            <h3 className="text-sm font-medium text-ink">
              {failures.length === 1
                ? '1 contrast problem'
                : `${failures.length} contrast problems`}
            </h3>
          </>
        )}
      </header>

      <ul className="mt-3 space-y-2">
        {checks.map((check) => (
          <li
            key={check.id}
            className={cn(
              'flex items-start gap-3 rounded-md border p-2.5 text-sm',
              check.passes
                ? 'border-line bg-surface-muted'
                : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40',
            )}
          >
            {/* A live sample of the actual pair. Seeing it is usually faster
                than reading the ratio. Inline styles because these are runtime
                colours — no Tailwind class can express them. */}
            <span
              className="mt-0.5 flex h-6 w-10 shrink-0 items-center justify-center rounded border border-line text-[10px] font-semibold"
              style={{ backgroundColor: check.background, color: check.foreground }}
              aria-hidden="true"
            >
              Aa
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-ink">{check.label}</span>
              <span
                className={cn(
                  'block text-xs',
                  check.passes ? 'text-ink-muted' : 'text-red-700 dark:text-red-300',
                )}
              >
                {formatRatio(check.ratio)}
                {check.passes
                  ? ` — meets ${formatRatio(check.required)}`
                  : ` — needs ${formatRatio(check.required)}. ${check.hint}`}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
